import "./lib/theme-init.ts";
import "./i18n/index.ts";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import * as Sentry from "@sentry/react";
import { ThemeProvider } from "./components/theme-provider.tsx";
import { Toaster } from "./components/ui/sonner.tsx";
import { RootErrorFallback } from "./components/root-error-fallback.tsx";
import { UpdateOverlay } from "./components/vroom/update-overlay.tsx";
import { initSentry } from "./lib/sentry.ts";
import { persister } from "./lib/query-persister.ts";
import { APP_VERSION } from "./lib/build-version.ts";
import { routeTree } from "./routeTree.gen.ts";
import "./styles.css";

initSentry();

const queryClient = new QueryClient();
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultViewTransition: true,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Missing #root element");

createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: 24 * 3600 * 1000,
          buster: APP_VERSION,
          dehydrateOptions: {
            shouldDehydrateQuery: (q) => {
              const root = q.queryKey?.[0] as string | undefined;
              return (
                root === "hours-entries" ||
                root === "trip-declarations" ||
                root === "lodging-declarations" ||
                root === "workorders" ||
                root === "me" ||
                root === "change-log" ||
                root === "addresses" ||
                root === "colleagues"
              );
            },
          },
        }}
      >
        <Sentry.ErrorBoundary fallback={<RootErrorFallback />}>
          <RouterProvider router={router} />
        </Sentry.ErrorBoundary>
        <Toaster />
        <UpdateOverlay />
        {import.meta.env.DEV ? <ReactQueryDevtools buttonPosition="bottom-left" /> : null}
      </PersistQueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
